import { useState, useMemo, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Inbox, Download, ArrowRight, Eye, Search, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { api, downloadBlob } from '@/api/client';

interface BucketRow {
  id: string; merchantId: number; merchant?: { id: number; companyName: string };
  receivedAt: string; status: string; routedAs?: string; routedOrderId?: string;
  customerName?: string; customerMobile?: string; customerAltMobile?: string; customerEmail?: string;
  customerAddress?: string; customerCity?: string; customerState?: string; customerPincode?: string;
  totalBoxes: number; productWeightKgs?: number; channelType?: string; channelOrderId?: string;
  freightCollectionType?: number; pickupType?: number; insuranceType?: number; isFragile?: number;
  invoiceData?: any[]; dimensionData?: any[]; subordersData?: any[];
}

const STATUS_COLORS: Record<string, string> = {
  PENDING: 'bg-amber-50 text-amber-700 border-amber-200',
  ROUTED_B2C: 'bg-blue-50 text-blue-700 border-blue-200',
  ROUTED_B2B: 'bg-violet-50 text-violet-700 border-violet-200',
  REJECTED: 'bg-rose-50 text-rose-700 border-rose-200',
  DUPLICATE: 'bg-slate-100 text-slate-600 border-slate-200',
};

// Fields the order-create form needs — flag any that arrived empty in the merchant's bucket payload,
// so the reviewer sees at a glance what they'll have to fill in before the order can be booked.
const REQUIRED_FIELDS: Array<[keyof BucketRow, string]> = [
  ['customerName', 'Name'],
  ['customerMobile', 'Mobile'],
  ['customerAddress', 'Address'],
  ['customerPincode', 'Pincode'],
  ['customerCity', 'City'],
  ['customerState', 'State'],
  ['productWeightKgs', 'Weight'],
];
function missingFields(b: BucketRow): string[] {
  const miss: string[] = [];
  for (const [k, label] of REQUIRED_FIELDS) {
    const v = b[k];
    if (v === null || v === undefined || String(v).trim() === '') miss.push(label);
  }
  // Present-but-invalid still needs the reviewer's attention.
  if (b.customerMobile && String(b.customerMobile).replace(/\D/g, '').length < 10) miss.push('Mobile (short)');
  if (b.customerPincode && !/^\d{6}$/.test(String(b.customerPincode))) miss.push('Pincode (invalid)');
  return miss;
}

const num = (v: any): number | undefined => { const n = Number(v); return Number.isFinite(n) && v !== '' && v !== null ? n : undefined; };

// Invoice / value helpers — the merchant payload carries invoice rows in `invoiceData`
// (product_invoice_value, invoice_no, order_no, eway_bill_no).
const invoiceRows = (b: BucketRow): any[] => (Array.isArray(b.invoiceData) ? b.invoiceData : []);
const invoiceTotal = (b: BucketRow): number => invoiceRows(b).reduce((s, x) => s + (num(x.product_invoice_value) ?? 0), 0);
const fmtINR = (n?: number | null) => (n === undefined || n === null ? '—' : `₹${Number(n).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`);
const FREIGHT_LABEL: Record<number, string> = { 1: 'Paid', 2: 'To-Pay', 3: 'TBB' };
const PICKUP_LABEL: Record<number, string> = { 1: 'Pickup', 2: 'Self-drop' };
const INSURANCE_LABEL: Record<number, string> = { 1: 'Owner Risk', 2: 'Carrier Risk' };

// ── Bucket payload → order-shaped object the create forms already know how to pre-fill from
//    (same shape their "Duplicate Order" flow consumes). ────────────────────────────────────
function bucketToB2cOrder(b: BucketRow) {
  const subs = Array.isArray(b.subordersData) ? b.subordersData : [];
  const invs = Array.isArray(b.invoiceData) ? b.invoiceData : [];
  const dim = Array.isArray(b.dimensionData) ? b.dimensionData[0] : null;
  const base = subs.length ? subs : invs;
  const products = base.map((s: any, i: number) => ({
    name: s.description ?? s.product_name ?? '',
    project: '',
    qty: Math.max(1, Math.round(num(s.count ?? s.qty) ?? 1)),
    value: num(invs[i]?.product_invoice_value ?? s.product_invoice_value),
    ewayBillNo: invs[i]?.eway_bill_no ?? '',
  }));
  return {
    merchantId: b.merchantId,
    orderNo: b.channelOrderId ?? invs[0]?.order_no ?? '',
    // The merchant's INVOICE number, which arrives in the same `invoice_data` row as `order_no` and
    // is a different document. Carried through so the created order stores it and the tracking
    // webhook can send a real `invoice_no` instead of falling back to the order number.
    invoiceNo: invs.find((iv: any) => String(iv?.invoice_no ?? '').trim())?.invoice_no ?? '',
    deliveryType: 'PREPAID',
    codValue: 0,
    customerMobile: b.customerMobile ?? '',
    customerName: b.customerName ?? '',
    customerEmail: b.customerEmail ?? '',
    customerAddress: b.customerAddress ?? '',
    customerPincode: b.customerPincode ?? '',
    customerCity: b.customerCity ?? '',
    customerState: b.customerState ?? '',
    products: products.length ? products : [{ qty: 1 }],
    dimensions: {
      length: num(dim?.length_in_cms),
      breadth: num(dim?.breadth_in_cms),
      height: num(dim?.height_in_cms),
      weight: num(b.productWeightKgs),
      isFragile: b.isFragile === 1 ? true : (b.isFragile === 0 ? false : undefined),
    },
    chargeableWeight: num(b.productWeightKgs),
  };
}
function bucketToB2bOrder(b: BucketRow) {
  const subs = Array.isArray(b.subordersData) ? b.subordersData : [];
  const invs = Array.isArray(b.invoiceData) ? b.invoiceData : [];
  const dimData = Array.isArray(b.dimensionData) ? b.dimensionData : [];
  const subOrders = subs.map((s: any) => ({ description: s.description ?? '', clientRefId: s.ident ?? '', qty: Math.max(1, Math.round(num(s.count) ?? 1)) }));
  const dimensions = dimData.map((dd: any) => ({ qty: Math.max(1, Math.round(num(dd.qty) ?? 1)), length: num(dd.length_in_cms) ?? 0, breadth: num(dd.breadth_in_cms) ?? 0, height: num(dd.height_in_cms) ?? 0, weight: 0 }));
  const invoiceDetails = invs.map((iv: any) => ({ productInvoiceValue: num(iv.product_invoice_value), invoiceNumber: iv.invoice_no ?? iv.order_no ?? '', ewayBillNumber: iv.eway_bill_no ?? '' }));
  return {
    lrGenerateType: '1',
    lrNo: '',
    freightCollection: '',
    pickupType: '',
    insuranceType: b.insuranceType === 2 ? 'CARRIER_RISK' : 'OWNER_RISK',
    merchantId: b.merchantId,
    customerMobile: b.customerMobile ?? '',
    customerName: b.customerName ?? '',
    customerAddress: b.customerAddress ?? '',
    customerPincode: b.customerPincode ?? '',
    customerCity: b.customerCity ?? '',
    customerState: b.customerState ?? '',
    products: [{ name: subs[0]?.description ?? '', project: '', weight: num(b.productWeightKgs), isFragile: b.isFragile === 1 }],
    chargeableWeight: num(b.productWeightKgs),
    subOrders: subOrders.length ? subOrders : [{}],
    dimensions: dimensions.length ? dimensions : [{}],
    userRemarks: JSON.stringify({ invoiceDetails: invoiceDetails.length ? invoiceDetails : [{}] }),
    deliveryType: 'PREPAID',
  };
}

/**
 * One page, two audiences.
 *
 * `variant="merchant"` renders the SAME table inside the merchant portal, including **Move to
 * B2C / B2B** — a merchant ships their own drops exactly as our team does. What is dropped is only
 * what is ours rather than theirs: the merchant filter and column (there is only ever one merchant
 * in their list), and Reject, which is a decision about whether we will carry the shipment at all.
 *
 * Deliberately NOT a copied component. The tenancy that keeps this safe lives in the API — every
 * `/orders-bucket` route pins a merchant identity to its own id from the token and answers 404 for
 * anyone else's entry — and a second component would be a second place for the request shape to
 * drift away from that.
 */
export function OrdersBucketPage({ variant = 'admin' }: { variant?: 'admin' | 'merchant' } = {}) {
  const isMerchant = variant === 'merchant';
  const nav = useNavigate();
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [merchantId, setMerchantId] = useState<number | ''>('');
  const [page, setPage] = useState(1);
  const pageSize = 50;
  const [openRow, setOpenRow] = useState<BucketRow | null>(null);

  const params = useMemo(() => {
    const p: any = { page, pageSize };
    if (search) p.search = search;
    if (status) p.status = status;
    if (merchantId) p.merchantId = merchantId;
    return p;
  }, [search, status, merchantId, page]);

  const { data, isLoading } = useQuery({
    queryKey: ['orders-bucket', params],
    queryFn: async () => (await api.get('/orders-bucket', { params })).data as { total: number; items: BucketRow[] },
  });
  const { data: merchants = [] } = useQuery({
    queryKey: ['merchants-light'],
    queryFn: async () => (await api.get('/merchants')).data,
    // A merchant has no merchant picker to fill, and the list of every merchant on the platform is
    // not theirs to fetch.
    enabled: !isMerchant,
  });

  // "Move to B2C / B2B" — open the matching create-order form pre-filled from the bucket payload.
  // The form marks this bucket routed (POST /orders-bucket/:id/link) once the order is saved.
  const moveTo = (b: BucketRow, as: 'B2C' | 'B2B') => {
    if (b.status !== 'PENDING') { toast.error(`Already routed (${b.status.replace('_', ' ')})`); return; }
    const path = as === 'B2C' ? '/orders-b2c/new' : '/orders-b2b/new';
    const duplicateFrom = as === 'B2C' ? bucketToB2cOrder(b) : bucketToB2bOrder(b);
    nav(path, { state: { duplicateFrom, bucketId: b.id, bucketAs: as } });
  };

  const exportCsv = async () => {
    const qs = new URLSearchParams();
    if (status) qs.set('status', status);
    if (merchantId) qs.set('merchantId', String(merchantId));
    const q = qs.toString();
    try { await downloadBlob(`/orders-bucket/export${q ? `?${q}` : ''}`, `orders-bucket-${new Date().toISOString().slice(0, 10)}.csv`); }
    catch (e: any) { toast.error(e?.response?.data?.message ?? 'Export failed'); }
  };

  const total = data?.total ?? 0;
  const items = data?.items ?? [];
  const lastPage = Math.max(1, Math.ceil(total / pageSize));
  const merchantList = Array.isArray(merchants) ? merchants : (merchants as any).items ?? [];
  // Kept next to the header definition it has to match — a hard-coded colSpan silently misaligns the
  // "no rows" cell the moment a column is added or dropped.
  const cols = isMerchant ? 10 : 11;

  // Amber for an empty payload cell so gaps are obvious at a glance.
  const cell = (v: any) => v === null || v === undefined || String(v).trim() === ''
    ? <span className="text-amber-600 font-medium">—</span>
    : <span>{v}</span>;

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2"><Inbox size={22} /> Orders Bucket</h1>
          <p className="text-sm text-slate-500 mt-0.5">
            {isMerchant
              ? 'Orders your system pushed to our API — review and move each one to B2C or B2B to ship it'
              : 'Incoming orders from merchant API — review and move to B2C or B2B'}
          </p>
        </div>
        <button onClick={exportCsv} className="px-3 py-2 border border-slate-300 text-sm rounded flex items-center gap-1.5 hover:bg-slate-50"><Download size={14} /> Export CSV</button>
      </div>

      <div className={`grid ${isMerchant ? 'grid-cols-3' : 'grid-cols-4'} gap-3 mb-4`}>
        <div className="relative">
          <Search size={13} className="absolute left-2 top-2.5 text-slate-400" />
          <input value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} placeholder="Customer / mobile / order no..."
            className="w-full border border-slate-300 rounded pl-7 pr-2 py-1.5 text-sm" />
        </div>
        {!isMerchant && (
          <select value={merchantId} onChange={(e) => { setMerchantId(e.target.value ? Number(e.target.value) : ''); setPage(1); }} className="border border-slate-300 rounded px-2 py-1.5 text-sm">
            <option value="">All merchants</option>
            {merchantList.map((m: any) => <option key={m.id} value={m.id}>#{m.id} · {m.companyName}</option>)}
          </select>
        )}
        <select value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }} className="border border-slate-300 rounded px-2 py-1.5 text-sm">
          <option value="">All statuses</option>
          <option value="PENDING">Pending</option>
          <option value="ROUTED_B2C">Routed B2C</option>
          <option value="ROUTED_B2B">Routed B2B</option>
          <option value="REJECTED">Rejected</option>
        </select>
        <button onClick={() => { setSearch(''); setStatus(''); setMerchantId(''); setPage(1); }} className="text-sm border border-slate-300 rounded px-3 py-1.5 hover:bg-slate-50">Reset</button>
      </div>

      <div className="bg-white border border-slate-200 rounded overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs text-slate-600">
            <tr>
              <th className="text-left px-3 py-2">Received</th>
              {!isMerchant && <th className="text-left px-3 py-2">Merchant</th>}
              <th className="text-left px-3 py-2">Customer</th>
              <th className="text-left px-3 py-2">Pin · City</th>
              <th className="text-center px-3 py-2">Boxes</th>
              <th className="text-center px-3 py-2">Wt (kg)</th>
              <th className="text-left px-3 py-2">Channel Order</th>
              <th className="text-right px-3 py-2">Invoice ₹</th>
              <th className="text-left px-3 py-2">Missing</th>
              <th className="text-center px-3 py-2">Status</th>
              <th className="text-right px-3 py-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && <tr><td colSpan={cols} className="text-center py-6 text-slate-400">Loading…</td></tr>}
            {!isLoading && items.length === 0 && <tr><td colSpan={cols} className="text-center py-6 text-slate-400">No bucket entries</td></tr>}
            {items.map(b => {
              const miss = missingFields(b);
              return (
              <tr key={b.id} className="border-t hover:bg-slate-50">
                <td className="px-3 py-2 text-xs whitespace-nowrap">{new Date(b.receivedAt).toLocaleString('en-IN')}</td>
                {!isMerchant && <td className="px-3 py-2">{b.merchant?.companyName ?? `#${b.merchantId}`}</td>}
                <td className="px-3 py-2">
                  <div className="font-medium">{cell(b.customerName)}</div>
                  <div className="text-xs text-slate-400 font-mono">{cell(b.customerMobile)}</div>
                </td>
                <td className="px-3 py-2 text-xs"><span className="font-mono">{cell(b.customerPincode)}</span> · {cell(b.customerCity)}</td>
                <td className="px-3 py-2 text-center">{b.totalBoxes}</td>
                <td className="px-3 py-2 text-center">{cell(b.productWeightKgs)}</td>
                <td className="px-3 py-2 text-xs font-mono">{cell(b.channelOrderId)}</td>
                <td className="px-3 py-2 text-right tabular-nums whitespace-nowrap">{invoiceTotal(b) > 0 ? fmtINR(invoiceTotal(b)) : <span className="text-amber-600 font-medium">—</span>}</td>
                <td className="px-3 py-2">
                  {miss.length === 0
                    ? <span className="text-[10px] text-emerald-600">Complete</span>
                    : <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded border bg-amber-50 text-amber-700 border-amber-200" title={`Empty in payload: ${miss.join(', ')}`}>
                        <AlertTriangle size={11} /> {miss.length} missing
                      </span>}
                  {miss.length > 0 && <div className="text-[10px] text-amber-600 mt-0.5 max-w-[160px] truncate" title={miss.join(', ')}>{miss.join(', ')}</div>}
                </td>
                <td className="px-3 py-2 text-center"><span className={`text-[10px] px-2 py-0.5 rounded border ${STATUS_COLORS[b.status] ?? ''}`}>{b.status.replace('_', ' ')}</span></td>
                <td className="px-3 py-2 text-right">
                  {b.status === 'PENDING' ? (
                    <div className="inline-flex gap-1">
                      <button onClick={() => moveTo(b, 'B2C')} className="text-xs px-2 py-1 bg-blue-50 text-blue-700 rounded border border-blue-200 hover:bg-blue-100">→ B2C</button>
                      <button onClick={() => moveTo(b, 'B2B')} className="text-xs px-2 py-1 bg-violet-50 text-violet-700 rounded border border-violet-200 hover:bg-violet-100">→ B2B</button>
                      <button onClick={() => setOpenRow(b)} className="text-xs px-2 py-1 border border-slate-200 rounded hover:bg-slate-50"><Eye size={12} /></button>
                    </div>
                  ) : (
                    <div className="inline-flex gap-1 items-center">
                      {b.routedOrderId && (
                        // The portals mount the order detail at different paths, so the link is built
                        // for whichever shell this is rendered in.
                        <a
                          href={isMerchant
                            ? `/orders-${(b.routedAs ?? 'B2C').toLowerCase()}/${b.routedOrderId}`
                            : `/orders/${b.routedOrderId}`}
                          className="text-xs text-indigo-600"
                        >Order #{b.routedOrderId}</a>
                      )}
                      <button onClick={() => setOpenRow(b)} className="text-xs px-2 py-1 border border-slate-200 rounded hover:bg-slate-50"><Eye size={12} /></button>
                    </div>
                  )}
                </td>
              </tr>
            ); })}
          </tbody>
        </table>
      </div>
      <div className="flex items-center justify-between mt-3 text-sm">
        <div className="text-slate-500">Page {page} of {lastPage} · {total} total</div>
        <div className="flex items-center gap-2">
          <button disabled={page <= 1} onClick={() => setPage(page - 1)} className="px-2 py-1 border rounded disabled:opacity-30">Prev</button>
          <button disabled={page >= lastPage} onClick={() => setPage(page + 1)} className="px-2 py-1 border rounded disabled:opacity-30">Next</button>
        </div>
      </div>

      {openRow && (() => {
        const miss = missingFields(openRow);
        return (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-start justify-center pt-12" onClick={() => setOpenRow(null)}>
          <div className="bg-white rounded-lg w-[720px] max-h-[88vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="bg-slate-800 text-white px-5 py-3 flex justify-between"><h3>Bucket #{openRow.id}{!isMerchant && ` · ${openRow.merchant?.companyName ?? ''}`}</h3><button onClick={() => setOpenRow(null)}>×</button></div>
            <div className="p-5 space-y-3">
              {miss.length > 0 && (
                <div className="text-sm rounded-md border border-amber-300 bg-amber-50 text-amber-800 px-3 py-2 flex items-start gap-2">
                  <AlertTriangle size={15} className="mt-0.5 shrink-0" />
                  <span>
                    This payload is missing <b>{miss.join(', ')}</b>.{' '}
                    {isMerchant
                      ? 'You’ll be asked for them on the create-order form after choosing B2C or B2B — send them with the order next time and there is nothing left to fill in.'
                      : 'You’ll complete these on the create-order form after choosing B2C or B2B.'}
                  </span>
                </div>
              )}
              {(() => {
                const invs = invoiceRows(openRow);
                const subs = Array.isArray(openRow.subordersData) ? openRow.subordersData : [];
                const dims = Array.isArray(openRow.dimensionData) ? openRow.dimensionData : [];
                const total = invoiceTotal(openRow);
                return (<>
                  {/* Customer */}
                  <Section title="Customer">
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <KV label="Name" value={openRow.customerName} />
                      <KV label="Mobile" value={openRow.customerMobile} />
                      <KV label="Alt Mobile" value={openRow.customerAltMobile} />
                      <KV label="Email" value={openRow.customerEmail} />
                      <KV label="Address" value={openRow.customerAddress} />
                      <KV label="City · State · Pincode" value={`${openRow.customerCity ?? '—'} · ${openRow.customerState ?? '—'} · ${openRow.customerPincode ?? '—'}`} />
                    </div>
                  </Section>

                  {/* Shipment */}
                  <Section title="Shipment">
                    <div className="grid grid-cols-3 gap-3 text-sm">
                      <KV label="Boxes" value={openRow.totalBoxes} />
                      <KV label="Weight (kg)" value={openRow.productWeightKgs} />
                      <KV label="Channel Order" value={openRow.channelOrderId} />
                      <KV label="Fragile" value={openRow.isFragile === 1 ? 'Yes' : openRow.isFragile === 0 ? 'No' : undefined} />
                      <KV label="Insurance" value={openRow.insuranceType != null ? (INSURANCE_LABEL[openRow.insuranceType] ?? openRow.insuranceType) : undefined} />
                      <KV label="Freight" value={openRow.freightCollectionType != null ? (FREIGHT_LABEL[openRow.freightCollectionType] ?? openRow.freightCollectionType) : undefined} />
                      <KV label="Pickup" value={openRow.pickupType != null ? (PICKUP_LABEL[openRow.pickupType] ?? openRow.pickupType) : undefined} />
                    </div>
                  </Section>

                  {/* Invoice */}
                  <Section title={`Invoice — total ${fmtINR(total)}`}>
                    {invs.length === 0 ? <div className="text-xs text-amber-600">No invoice data in payload</div> : (
                      <div className="overflow-x-auto border border-slate-200 rounded">
                        <table className="w-full text-xs">
                          <thead className="bg-slate-50 text-slate-500"><tr>
                            <th className="text-left px-2 py-1.5">Order No</th><th className="text-left px-2 py-1.5">Invoice No</th>
                            <th className="text-right px-2 py-1.5">Value</th><th className="text-left px-2 py-1.5">Eway Bill</th>
                          </tr></thead>
                          <tbody>
                            {invs.map((iv: any, i: number) => (
                              <tr key={i} className="border-t border-slate-100">
                                <td className="px-2 py-1.5 font-mono">{iv.order_no ?? '—'}</td>
                                <td className="px-2 py-1.5 font-mono">{iv.invoice_no ?? '—'}</td>
                                <td className="px-2 py-1.5 text-right tabular-nums">{fmtINR(num(iv.product_invoice_value))}</td>
                                <td className="px-2 py-1.5 font-mono">{iv.eway_bill_no || '—'}</td>
                              </tr>
                            ))}
                          </tbody>
                          <tfoot><tr className="border-t bg-slate-50 font-semibold"><td className="px-2 py-1.5" colSpan={2}>Total</td><td className="px-2 py-1.5 text-right tabular-nums">{fmtINR(total)}</td><td /></tr></tfoot>
                        </table>
                      </div>
                    )}
                  </Section>

                  {/* Products / sub-orders */}
                  {subs.length > 0 && (
                    <Section title={`Products (${subs.length})`}>
                      <div className="overflow-x-auto border border-slate-200 rounded">
                        <table className="w-full text-xs">
                          <thead className="bg-slate-50 text-slate-500"><tr>
                            <th className="text-left px-2 py-1.5">Description</th><th className="text-left px-2 py-1.5">SKU / Ident</th><th className="text-right px-2 py-1.5">Qty</th>
                          </tr></thead>
                          <tbody>
                            {subs.map((s: any, i: number) => (
                              <tr key={i} className="border-t border-slate-100">
                                <td className="px-2 py-1.5">{s.description ?? s.product_name ?? '—'}</td>
                                <td className="px-2 py-1.5 font-mono">{s.ident ?? '—'}</td>
                                <td className="px-2 py-1.5 text-right">{num(s.count ?? s.qty) ?? '—'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </Section>
                  )}

                  {/* Dimensions */}
                  {dims.length > 0 && (
                    <Section title={`Dimensions (${dims.length})`}>
                      <div className="overflow-x-auto border border-slate-200 rounded">
                        <table className="w-full text-xs">
                          <thead className="bg-slate-50 text-slate-500"><tr>
                            <th className="text-right px-2 py-1.5">L (cm)</th><th className="text-right px-2 py-1.5">B (cm)</th><th className="text-right px-2 py-1.5">H (cm)</th><th className="text-right px-2 py-1.5">Qty</th>
                          </tr></thead>
                          <tbody>
                            {dims.map((d: any, i: number) => (
                              <tr key={i} className="border-t border-slate-100 tabular-nums">
                                <td className="px-2 py-1.5 text-right">{num(d.length_in_cms) ?? '—'}</td>
                                <td className="px-2 py-1.5 text-right">{num(d.breadth_in_cms) ?? '—'}</td>
                                <td className="px-2 py-1.5 text-right">{num(d.height_in_cms) ?? '—'}</td>
                                <td className="px-2 py-1.5 text-right">{num(d.qty) ?? '—'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </Section>
                  )}
                </>);
              })()}
              <div className="flex gap-2 pt-4 border-t">
                <button onClick={() => moveTo(openRow, 'B2C')} className="flex-1 bg-blue-600 text-white py-2 rounded text-sm hover:bg-blue-700 flex items-center justify-center gap-2">
                  <ArrowRight size={14} /> Move to B2C
                </button>
                <button onClick={() => moveTo(openRow, 'B2B')} className="flex-1 bg-violet-600 text-white py-2 rounded text-sm hover:bg-violet-700 flex items-center justify-center gap-2">
                  <ArrowRight size={14} /> Move to B2B
                </button>
              </div>
            </div>
          </div>
        </div>
      ); })()}
    </div>
  );
}

function KV({ label, value }: { label: string; value?: any }) {
  const empty = value === null || value === undefined || String(value).trim() === '';
  return (<div><div className="text-xs text-slate-500">{label}</div><div className={`font-medium ${empty ? 'text-amber-600' : ''}`}>{empty ? '—' : value}</div></div>);
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="space-y-2">
      <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">{title}</div>
      {children}
    </div>
  );
}
